/**
 * The `callbacks` slice of the SimpleTable context value.
 *
 * Split out of `SimpleTableContextValue.ts` (module-size lane L17). Behaviour is
 * pinned by `tests/universal/simple-table-context-value.test.ts`.
 *
 * This is an explicit ALLOW-LIST, not a spread: `useSimpleTableCallbacks`
 * exposes factories (`createHandleSelectAll`, `createHandleBulkAction`) and
 * intermediate handlers (`addSortColumn`, `applyMultiColumnSortCallback`,
 * the un-suffixed scroll callbacks) that consumers must NOT reach for. Keeping
 * the list literal is what stops a new internal helper leaking into the public
 * context surface by accident.
 */

/**
 * The composed callback set from `useSimpleTableCallbacks`. Two callback-hook
 * implementations feed it (the `.ts` and `.tsx` variants expose overlapping but
 * not identical keys), so it arrives as a map of callables rather than one
 * hook's `ReturnType`; each entry is assigned to its precise signature when the
 * caller narrows the slice to the context type.
 */
export type ComposedTableCallbacks = Record<string, (...args: never[]) => unknown>;

export function buildCallbacksSlice(callbacks: ComposedTableCallbacks) {
  const {
    handleColumnResizeCallback,
    resetColumnWidthsCallback,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
    handleSort,
    handleSelectItem,
    handleBulkEditField,
    handleBulkEditAllFiltered,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    removeSortColumn,
    clearAllSorting,
    handleSelectAllPages,
    handleFetchAllPages,
    handleSelectAll,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
  } = callbacks;

  return {
    handleSelectItem,
    handleSelectAll,
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
    handleSort,
    removeSortColumn,
    clearAllSorting,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    resetColumnWidthsCallback,
    handleColumnResizeCallback,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleBulkEditField,
    handleBulkEditAllFiltered,
    handleSelectAllPages,
    handleFetchAllPages,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
  };
}
